import { Field, Image, Stack } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UnauthLayout } from "layouts/UnauthLayout";
import { useBrand } from "brand/BrandConfig";
import {
  CoreAPIError,
  RESET_PASSWORD_ROUTE,
  coreapi,
  forgetResetToken,
  heldResetToken,
  markResetSaveUnclear,
  takeResetTokenFromURL,
  wasResetSaveUnclear,
} from "services/coreapi";
import {
  defaultLoginUI,
  FORM_GAP,
  FormError,
  LoginNotice,
  LoginUI,
} from "./loginUI";
import { useAuthStore } from "stores/AuthStoreProvider";
import { FORGOT_PASSWORD_PATH } from "./useLoginForm";
import {
  checkNewPassword,
  describePasswordRefusal,
  passwordMatch,
} from "./passwordRules";

const SAVE_FAILED = "Couldn't save your password. Try again.";
const TOO_MANY = "Too many attempts. Please wait a minute and try again.";
const IF_NOT_SAVED =
  'If it doesn\'t work at login, use "Forgot password?" to get a new link.';

export const ResetPasswordPage = observer(
  ({
    ui: uiOverrides,
  }: {
    /** App-specific look, same as `<LoginPage ui={…} />`. */
    ui?: Partial<LoginUI>;
  }) => {
    const brand = useBrand();
    const location = useLocation();
    const navigate = useNavigate();
    const isSignedIn = useAuthStore().isAuthenticated;

    const ui = React.useMemo<LoginUI>(
      () => ({ ...defaultLoginUI, ...uiOverrides }),
      [uiOverrides],
    );

    // The app entry already took the token out of the URL; the call here covers
    // an entry that didn't.
    const [token] = React.useState<string | null>(() => {
      takeResetTokenFromURL();
      return heldResetToken();
    });
    // The server sends a dead link here with `?error=INVALID_TOKEN`.
    const [isExpired, setIsExpired] = React.useState<boolean>(
      () => !token || new URLSearchParams(location.search).has("error"),
    );
    // The link is dead because an earlier save used it up, though its answer
    // never arrived: the password is set, and "expired" would be wrong.
    const [maybeSaved, setMaybeSaved] = React.useState<boolean>(
      () => isExpired && wasResetSaveUnclear(),
    );
    const [password, setPassword] = React.useState<string>("");
    const [repeat, setRepeat] = React.useState<string>("");
    const [hasLeftRepeat, setHasLeftRepeat] = React.useState<boolean>(false);
    // One eye for both boxes: either one shows or hides both.
    const [isRevealed, setIsRevealed] = React.useState<boolean>(false);
    const toggleReveal = () => setIsRevealed((v) => !v);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string | null>(null);
    // State updates land late: a ref stops a double click sending two requests
    // (the second would be refused as "used" and flash the expired view).
    const isSaving = React.useRef(false);

    // The address bar is clean, but React Router still remembers the first URL.
    // Drop it there too, so no redirect can write the token back.
    React.useEffect(() => {
      if (location.search) {
        navigate(RESET_PASSWORD_ROUTE, { replace: true });
      }
    }, [location.search, navigate]);

    React.useEffect(() => {
      if (isExpired) {
        forgetResetToken();
      }
    }, [isExpired]);

    async function save(): Promise<void> {
      if (!token || isSaving.current) {
        return;
      }
      const problem = checkNewPassword(password, repeat);
      setError(problem);
      if (problem) {
        return;
      }

      isSaving.current = true;
      setIsLoading(true);
      try {
        await coreapi.resetPasswordAndSignIn({ token, newPassword: password });
        // Full reload: the app boots from the new session, whoever was logged in before.
        window.location.replace("/");
      } catch (err) {
        console.error(err);
        isSaving.current = false;
        setIsLoading(false);

        const code = err instanceof CoreAPIError ? err.code : undefined;
        const status = err instanceof CoreAPIError ? err.status : undefined;
        const refusal = describePasswordRefusal(code);
        if (code === "INVALID_TOKEN") {
          setMaybeSaved(wasResetSaveUnclear());
          setIsExpired(true);
        } else if (refusal) {
          setError(refusal);
        } else if (status === 429) {
          setError(TOO_MANY);
        } else {
          // No clear answer: the server may have saved it all the same.
          markResetSaveUnclear();
          setError(SAVE_FAILED);
        }
      }
    }

    return (
      <UnauthLayout>
        <Image
          src={brand.logo}
          alt={`${brand.name} logo`}
          width={"120px"}
          mx="auto"
          mb={6}
        />

        {isExpired ? (
          <Stack
            width="full"
            as="form"
            gap={FORM_GAP}
            onSubmit={(e) => {
              e.preventDefault();
              // RouteGuard bounces a signed-in user off /login (a link they
              // just used signed them in), so the forgot form is out of reach.
              if (isSignedIn) {
                navigate("/", { replace: true });
              } else {
                navigate(maybeSaved ? "/login" : FORGOT_PASSWORD_PATH);
              }
            }}
          >
            {maybeSaved ? (
              <LoginNotice title="Your password was probably saved.">
                {IF_NOT_SAVED}
              </LoginNotice>
            ) : (
              <LoginNotice title="This link has expired.">
                Reset links work for 1 hour, and only once.
              </LoginNotice>
            )}

            <ui.SubmitButton isLoading={false} disabled={false}>
              {isSignedIn
                ? "Continue to the app"
                : maybeSaved
                  ? "Go to login"
                  : "Get a new link"}
            </ui.SubmitButton>
          </Stack>
        ) : (
          <Stack
            width="full"
            as="form"
            gap={FORM_GAP}
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <Field.Root gap={FORM_GAP}>
              <Field.Label
                width="100%"
                justifyContent="center"
                textAlign={"center"}
                color="whiteAlpha.700"
              >
                Choose a new password
              </Field.Label>

              <ui.Input
                type="password"
                name="new-password"
                autoComplete="new-password"
                placeholder="New password"
                isRevealed={isRevealed}
                onToggleReveal={toggleReveal}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError(null);
                }}
              />

              <ui.Input
                type="password"
                name="repeat-password"
                autoComplete="new-password"
                placeholder="Repeat password"
                isRevealed={isRevealed}
                onToggleReveal={toggleReveal}
                match={passwordMatch(password, repeat, hasLeftRepeat)}
                value={repeat}
                onChange={(e) => {
                  setRepeat(e.target.value);
                  setError(null);
                }}
                onBlur={() => setHasLeftRepeat(true)}
              />

              <FormError isValid={true} error={error} />
            </Field.Root>

            <ui.SubmitButton isLoading={isLoading} disabled={isLoading}>
              Save
            </ui.SubmitButton>
          </Stack>
        )}
      </UnauthLayout>
    );
  },
);
